"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import type React from "react";
import { fetchExchangeRates } from "./api";
import type { ExchangeRates, PriceInput, SupportedCurrency } from "./types";

// Cache rates for 24 hours
const STALE_TIME = 24 * 60 * 60 * 1000;

/**
 * Parse a numeric value from string or number input
 */
function parseNumericValue(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const parsed = typeof value === "string" ? parseFloat(value) : value;
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Normalize currency code to uppercase
 */
function normalizeCurrency(
  currency: string | null | undefined,
): SupportedCurrency {
  if (!currency) return "USDT";
  const upper = currency.toUpperCase();
  if (upper === "IRT" || upper === "TOMAN") return "IRT";
  if (upper === "USD" || upper === "USDT") return "USDT";
  if (upper === "UVM") return "UVM";
  return "USDT";
}

/**
 * Convert a value from its original currency to USD
 */
function convertToUsd(
  value: number,
  currency: SupportedCurrency,
  rates: ExchangeRates,
): number {
  switch (currency) {
    case "IRT":
    case "TOMAN":
      return rates.usdToIrt > 0 ? value / rates.usdToIrt : 0;
    case "UVM":
      return value * rates.uvmToUsd;
    default:
      return value;
  }
}

/**
 * Convert USD to IRT (Toman)
 */
function convertUsdToIrt(usdValue: number, rates: ExchangeRates): number {
  return usdValue * rates.usdToIrt;
}

/**
 * Convert USD to UVM
 */
function convertUsdToUvm(usdValue: number, rates: ExchangeRates): number {
  return rates.uvmToUsd > 0 ? usdValue / rates.uvmToUsd : 0;
}

/**
 * Format a number with تومان (or locale-equivalent) suffix
 * Uses "." as thousand separator and English digits
 */
function formatAsToman(value: number, tomanLabel = "تومان"): string {
  const rounded = Math.round(value);
  const formatted = rounded.toLocaleString("en-US"); // Uses "." as thousand separator
  return `${formatted} ${tomanLabel}`;
}

/**
 * Format price as IRT with BV - returns JSX similar to formatPrice
 * Output format: "2.300.000 تومان ≈ 300.000 تومان +"
 */
export function formatPriceAsIrt(
  input: PriceInput,
  rates: ExchangeRates | undefined,
  tomanLabel = "تومان",
): React.ReactNode {
  if (!rates) return "";
  if (!input.seller_share_unit || !input.seller_share_currency) return "";

  const unit = parseNumericValue(input.seller_share_unit);
  const currency = normalizeCurrency(input.seller_share_currency);
  const bv = parseNumericValue(input.bv);

  // Convert seller_share to IRT
  const unitInUsd = convertToUsd(unit, currency, rates);
  const unitInIrt = convertUsdToIrt(unitInUsd, rates);

  // Convert BV to IRT (BV is always in UVM)
  const bvInUsd = bv > 0 ? convertToUsd(bv, "UVM", rates) : 0;
  const bvInIrt = bvInUsd > 0 ? convertUsdToIrt(bvInUsd, rates) : 0;

  const bvText =
    bvInIrt > 0 ? (
      <span>
        <span>{formatAsToman(bvInIrt, tomanLabel)}</span>
        <span>+</span>
      </span>
    ) : null;

  return (
    <div className="flex items-center gap-0.5">
      {bvText}
      <span>{formatAsToman(unitInIrt, tomanLabel)}</span>
      {!input.hideIcon ? <span className="mt-0.5">≈</span> : null}
    </div>
  );
}

export function formatSvPrice(
  input: PriceInput,
  rates: ExchangeRates | undefined,
  tomanLabel = "تومان",
): React.ReactNode {
  if (!rates) return "";
  if (!input.seller_share_unit || !input.seller_share_currency) return "";

  const unit = parseNumericValue(input.seller_share_unit);
  if (unit < 0) return "";

  const currencyFrom = normalizeCurrency(input.seller_share_currency);
  const currencyTo = normalizeCurrency(
    input.seller_share_currency_to || currencyFrom,
  );

  const bv = parseNumericValue(input.bv);

  // FROM → USD
  const unitInUsd = convertToUsd(unit, currencyFrom, rates);

  // USD → TO
  let finalValue: number;
  switch (currencyTo) {
    case "IRT":
    case "TOMAN":
      finalValue = convertUsdToIrt(unitInUsd, rates);
      break;
    case "UVM":
      finalValue = convertUsdToUvm(unitInUsd, rates);
      break;
    default:
      finalValue = unitInUsd;
  }

  const bvText =
    bv > 0 ? (
      <span>
        <span dir="ltr">{bv} UVM</span>
        <span>+</span>
      </span>
    ) : null;

  return (
    <div className="flex items-center gap-0.5 font-inter">
      {bvText}
      <span dir="ltr">
        {currencyTo === "IRT" || currencyTo === "TOMAN"
          ? formatAsToman(finalValue, tomanLabel)
          : `${finalValue.toLocaleString("en-US")} ${currencyTo}`}
      </span>
      {!input.hideIcon ? <span className="mb-0.5">≈</span> : null}
    </div>
  );
}

export function formatSvPriceAsObject(
  input: PriceInput,
  rates: ExchangeRates | undefined,
): { bv: number; finalValue: number } {
  if (!rates) return { bv: 0, finalValue: 0 };

  if (!input.seller_share_unit || !input.seller_share_currency)
    return { bv: 0, finalValue: 0 };

  const unit = parseNumericValue(input.seller_share_unit);
  if (unit <= 0) return { bv: 0, finalValue: 0 };

  const currencyFrom = normalizeCurrency(input.seller_share_currency);
  const currencyTo = normalizeCurrency(
    input.seller_share_currency_to || currencyFrom,
  );

  const bv = parseNumericValue(input.bv);

  // FROM → USD
  const unitInUsd = convertToUsd(unit, currencyFrom, rates);

  // USD → TO
  let finalValue: number;
  switch (currencyTo) {
    case "IRT":
    case "TOMAN":
      finalValue = convertUsdToIrt(unitInUsd, rates);
      break;
    case "UVM":
      finalValue = convertUsdToUvm(unitInUsd, rates);
      break;
    default:
      finalValue = unitInUsd;
  }

  return { bv, finalValue };
}

/**
 * Normalize settlement currency to "UVM" | "USDT"
 */
function normalizeSettlementCurrency(
  currency: string | null | undefined,
): "UVM" | "USDT" {
  if (!currency) return "UVM";
  const upper = currency.toUpperCase();
  if (upper === "UVM") return "UVM";
  if (upper === "USDT" || upper === "USD") return "USDT";
  return "UVM";
}

/**
 * Convert a price to the settlement currency
 * This converts SV (seller_share_unit) from its original currency to the settlement currency
 * BV is always in UVM and returned separately
 *
 * @param input - Price input with seller_share_unit, seller_share_currency, and bv
 * @param settlementCurrency - The currency the seller wants to receive ("UVM" or "USDT" or string)
 * @param rates - Exchange rates
 * @returns Object with bv (always UVM) and svInSettlementCurrency
 */
export function convertPriceToSettlementCurrency(
  input: PriceInput,
  settlementCurrency: string | null | undefined,
  rates: ExchangeRates | undefined,
): { bv: number; svInSettlementCurrency: number } {
  if (!rates) return { bv: 0, svInSettlementCurrency: 0 };

  if (!input.seller_share_unit || !input.seller_share_currency)
    return { bv: 0, svInSettlementCurrency: 0 };

  const unit = parseNumericValue(input.seller_share_unit);
  if (unit <= 0) return { bv: 0, svInSettlementCurrency: 0 };

  const currencyFrom = normalizeCurrency(input.seller_share_currency);
  const bv = parseNumericValue(input.bv);
  const normalizedSettlement = normalizeSettlementCurrency(settlementCurrency);

  // Convert seller_share_unit from its original currency to USD
  const unitInUsd = convertToUsd(unit, currencyFrom, rates);

  // Convert USD to settlement currency
  let svInSettlementCurrency: number;
  if (normalizedSettlement === "UVM") {
    svInSettlementCurrency = convertUsdToUvm(unitInUsd, rates);
  } else {
    // USDT (same as USD)
    svInSettlementCurrency = unitInUsd;
  }

  return {
    bv: +bv.toFixed(2),
    svInSettlementCurrency: +svInSettlementCurrency.toFixed(2),
  };
}

/**
 * Hook that fetches exchange rates and provides formatPriceAsIrt function
 *
 * @example
 * const { formatPrice, isLoading, rates } = usePriceConverter();
 *
 * // Use in JSX
 * <div>{formatPrice({ seller_share_unit: 100, seller_share_currency: "UVM", bv: 50 })}</div>
 */
export function usePriceConverter() {
  const {
    data: rates,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["exchange-rates"],
    queryFn: fetchExchangeRates,
    staleTime: STALE_TIME,
  });

  const tCurrency = useTranslations("common.currency");

  /**
   * Format a price input as IRT (تومان) - wrapper around formatPriceAsIrt
   */
  const formatPrice = (input: PriceInput): React.ReactNode => {
    return formatPriceAsIrt(input, rates, tCurrency("toman"));
  };

  const formatFinalPrice = (input: PriceInput): React.ReactNode => {
    return formatSvPrice(input, rates, tCurrency("toman"));
  };

  const formatFinalPriceAsObject = (
    input: PriceInput,
  ): { bv: number; finalValue: number } => {
    return formatSvPriceAsObject(input, rates);
  };

  const convertToSettlementCurrency = (
    input: PriceInput,
    settlementCurrency: string | null | undefined,
  ): { bv: number; svInSettlementCurrency: number } => {
    return convertPriceToSettlementCurrency(input, settlementCurrency, rates);
  };

  return {
    /** Format a price input as IRT (تومان) */
    formatPrice,
    formatFinalPrice,
    formatFinalPriceAsObject,
    /** Convert a price to the settlement currency (UVM or USDT) */
    convertToSettlementCurrency,

    /** Current exchange rates */
    rates,
    /** Whether rates are loading */
    isLoading,
    /** Error if fetching rates failed */
    error,
    /** Refetch exchange rates */
    refetch,
  };
}
